import React, { useMemo, useState } from 'react';
import { Box, Chip, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import GlassPaper from '../../components/GlassPaper';
import ChartStack from './components/ChartStack';
import ControlSidebar from './components/ControlSidebar';
import FillsTable from './components/FillsTable';
import TelemetryPanel from './components/TelemetryPanel';
import TicksTable from './components/TicksTable';
import {
    useTickBacktest,
    useTickDates,
    useTickSeries,
    useTickSymbols,
} from './api';
import { pushRecent } from './recents';
import { useTickStudioUrl } from './useTickStudioUrl';
import './tick-studio.css';

export default function TickLakePage() {
    const [date, setDate] = useState('');
    const [symbol, setSymbol] = useState(null);
    const [symbolInput, setSymbolInput] = useState('');
    const [tf, setTf] = useState('1m');
    const [rth, setRth] = useState(true);
    const [threshold, setThreshold] = useState(0.3);
    const [thresholdInput, setThresholdInput] = useState(0.3);
    const [slippage, setSlippage] = useState(1.0);
    const [slippageInput, setSlippageInput] = useState(1.0);
    const [orderQty, setOrderQty] = useState(1);
    const [runBacktest, setRunBacktest] = useState(false);
    const [chartTab, setChartTab] = useState('overview');

    const { data: datesData } = useTickDates();
    const dates = datesData?.dates || [];

    const { effectiveDate } = useTickStudioUrl({
        dates,
        date,
        setDate,
        symbol,
        setSymbol,
        setSymbolInput,
        tf,
        setTf,
        rth,
        setRth,
    });

    const handleDateChange = (newDate) => {
        setDate(newDate);
        setSymbol(null);
        setSymbolInput('');
        setRunBacktest(false);
    };

    const handleSymbolChange = (next) => {
        setSymbol(next);
        setRunBacktest(false);
        if (next) pushRecent(next);
    };

    const { data: symbolsData, isFetching: symbolsLoading } = useTickSymbols(effectiveDate, symbolInput);
    const symbols = symbolsData?.symbols || [];

    const { data: series, isLoading: seriesLoading, isError: seriesError, error: seriesErr } =
        useTickSeries(symbol, effectiveDate, tf, rth);

    const obiPoints = useMemo(
        () => (series?.obi || []).map((r) => ({ ts: r.ts, value: r.obi })),
        [series],
    );

    const isBookless = useMemo(
        () => !!series && obiPoints.length > 0 && !obiPoints.some((p) => Math.abs(p.value) > 1e-9),
        [series, obiPoints],
    );

    const { data: bt, isFetching: btLoading } = useTickBacktest(
        symbol,
        effectiveDate,
        threshold,
        slippage,
        orderQty,
        rth,
        runBacktest && !isBookless,
    );

    const equityPoints = useMemo(
        () => (bt?.equity || []).map((r) => ({ ts: r.ts, value: r.equity })),
        [bt],
    );
    const metrics = bt?.metrics;

    const handleRunBacktest = () => {
        if (!symbol || isBookless) return;
        setRunBacktest(true);
    };

    return (
        <Box className="tick-studio" sx={{ px: { xs: 0.5, sm: 1 }, py: 1 }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Tick Studio
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    TrueData tick-by-tick lake — resampled OHLC, order-book imbalance, and event-driven backtests
                </Typography>
            </Box>

            <TelemetryPanel />

            <Box className="tick-studio__layout">
                <ControlSidebar
                    dates={dates}
                    effectiveDate={effectiveDate}
                    onDateChange={handleDateChange}
                    symbol={symbol}
                    symbolInput={symbolInput}
                    onSymbolChange={handleSymbolChange}
                    onSymbolInputChange={setSymbolInput}
                    symbols={symbols}
                    symbolsLoading={symbolsLoading}
                    tf={tf}
                    onTfChange={setTf}
                    rth={rth}
                    onRthChange={setRth}
                    isBookless={isBookless}
                    thresholdInput={thresholdInput}
                    onThresholdInputChange={setThresholdInput}
                    onThresholdCommit={setThreshold}
                    slippageInput={slippageInput}
                    onSlippageInputChange={setSlippageInput}
                    onSlippageCommit={setSlippage}
                    orderQty={orderQty}
                    onOrderQtyChange={setOrderQty}
                    onRunBacktest={handleRunBacktest}
                    backtestRunning={runBacktest && btLoading}
                    runBacktest={runBacktest}
                    metrics={metrics}
                />

                <Box className="tick-studio__main">
                    {symbol && isBookless && (
                        <Chip
                            label="Index — no order book; OBI backtest unavailable"
                            color="warning"
                            variant="outlined"
                            size="small"
                            sx={{ mb: 1 }}
                        />
                    )}

                    <GlassPaper sx={{ p: { xs: 1.25, sm: 1.75 }, minHeight: 320 }}>
                        {!symbol && (
                            <Typography variant="body2" color="text.secondary">
                                Pick a date and symbol to load candles.
                            </Typography>
                        )}
                        {symbol && (seriesLoading || (runBacktest && btLoading)) && (
                            <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                        )}
                        {symbol && seriesError && (
                            <Typography variant="body2" color="error.main">
                                {seriesErr.message}
                            </Typography>
                        )}
                        {symbol && series && (
                            <>
                                <Typography variant="subtitle2" gutterBottom>
                                    {symbol} · {effectiveDate} · {series.rows?.toLocaleString()} ticks →{' '}
                                    {series.candles?.length || 0} candles
                                </Typography>

                                <Tabs
                                    value={chartTab}
                                    onChange={(_, v) => setChartTab(v)}
                                    sx={{ minHeight: 36, mb: 1, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none' } }}
                                >
                                    <Tab label="Overview" value="overview" />
                                    <Tab label="Fills" value="fills" disabled={!runBacktest} />
                                    <Tab label="Ticks" value="ticks" />
                                </Tabs>

                                {chartTab === 'overview' && (
                                    <ChartStack
                                        candles={series.candles}
                                        obiPoints={obiPoints}
                                        equityPoints={equityPoints}
                                        fills={bt?.fills || []}
                                        isBookless={isBookless}
                                        showEquity={runBacktest}
                                    />
                                )}
                                {chartTab === 'fills' && <FillsTable fills={bt?.fills || []} />}
                                {chartTab === 'ticks' && (
                                    <TicksTable symbol={symbol} date={effectiveDate} />
                                )}
                            </>
                        )}
                    </GlassPaper>
                </Box>
            </Box>
        </Box>
    );
}
